/**
 * ServerUiDomainProvider — context-based domain component registration
 * and container scope.
 *
 * Consumer pages (e.g., PatientPage) wrap their content in this provider
 * and pass a map of type_key → domain component. ComponentSlot checks
 * this context before falling through to generic primitive rendering.
 *
 * The provider also carries `containerKey` and `entityId` so that
 * primitive renderers (e.g., TileRowRenderer) can dispatch actions
 * without prop-drilling through every layer of the rendering pipeline.
 *
 * The optional `skeletons` prop provides loading placeholders for lazy
 * components. Skeletons are resolved via a fallback chain:
 *   1. byTypeKey[component.type_key]
 *   2. byPrimitive[component.primitive]
 *   3. built-in DefaultLazyPlaceholder
 *
 * Usage:
 * ```tsx
 * <ServerUiDomainProvider
 *   components={domainComponents}
 *   containerKey="patient_page"
 *   entityId={patientId}
 * >
 *   <ComponentSlot ... />
 * </ServerUiDomainProvider>
 * ```
 */
import React, { createContext, useContext, useMemo } from 'react';

import type { ActionTargetMap, KnownTypeKey, Primitive, ServerUiComponent } from '../types';

type DomainComponentType = React.ComponentType<{ component: ServerUiComponent }>;

// KnownTypeKey gives autocomplete + typo protection when registering components.
// The index signature allows ComponentSlot to look up by arbitrary string without casting.
export type DomainComponentRegistry = Partial<Record<KnownTypeKey, DomainComponentType>> &
  Record<string, DomainComponentType | undefined>;

export type SkeletonProps = { component: ServerUiComponent };

export type SkeletonComponentMap = {
  byTypeKey?: Partial<Record<KnownTypeKey, React.ComponentType<SkeletonProps>>>;
  byPrimitive?: Partial<Record<Primitive, React.ComponentType<SkeletonProps>>>;
};

export type ServerUiScope = {
  containerKey: string | undefined;
  entityId: string | undefined;
};

interface ServerUiDomainProviderProps {
  components: DomainComponentRegistry;
  skeletons?: SkeletonComponentMap;
  actionTargets?: ActionTargetMap;
  containerKey?: string;
  entityId?: string;
  children: React.ReactNode;
}

const ServerUiDomainContext = createContext<DomainComponentRegistry>({});
const ServerUiSkeletonContext = createContext<SkeletonComponentMap | undefined>(undefined);
const ServerUiActionTargetContext = createContext<ActionTargetMap>({});
const ServerUiScopeContext = createContext<ServerUiScope>({
  containerKey: undefined,
  entityId: undefined,
});

export function ServerUiDomainProvider({
  components,
  skeletons,
  actionTargets,
  containerKey,
  entityId,
  children,
}: ServerUiDomainProviderProps) {
  const scope = useMemo(() => ({ containerKey, entityId }), [containerKey, entityId]);
  const targets = useMemo(() => actionTargets ?? {}, [actionTargets]);

  return (
    <ServerUiDomainContext.Provider value={components}>
      <ServerUiSkeletonContext.Provider value={skeletons}>
        <ServerUiActionTargetContext.Provider value={targets}>
          <ServerUiScopeContext.Provider value={scope}>
            {children}
          </ServerUiScopeContext.Provider>
        </ServerUiActionTargetContext.Provider>
      </ServerUiSkeletonContext.Provider>
    </ServerUiDomainContext.Provider>
  );
}

export function useDomainComponents(): DomainComponentRegistry {
  return useContext(ServerUiDomainContext);
}

export function useSkeletons(): SkeletonComponentMap | undefined {
  return useContext(ServerUiSkeletonContext);
}

export function useActionTargetOverrides(): ActionTargetMap {
  return useContext(ServerUiActionTargetContext);
}

export function useServerUiScope(): ServerUiScope {
  return useContext(ServerUiScopeContext);
}
